import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { fmtErr } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      nav(u.role === "handyman" ? "/handyman" : "/dashboard");
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Existing users keep whatever role they registered with; role hint only applies on first-time signup.
    sessionStorage.setItem("hm_pending_role", "student");
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-cream">
      <div className="w-full max-w-md bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-8 shadow-sm animate-fade-up">
        <h1 className="font-display text-3xl text-charcoal">Welcome back</h1>
        <p className="text-charcoal/60 mt-1 mb-6">Sign in to book your next move-in helper.</p>

        <Button onClick={googleLogin} variant="outline" data-testid="google-login-btn"
          className="w-full rounded-full border-charcoal/20 hover:bg-beige mb-5">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-4">
          <div className="h-px bg-line flex-1" />
          <span className="text-xs uppercase tracking-widest text-charcoal/50">or</span>
          <div className="h-px bg-line flex-1" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email" className="mt-1 rounded-xl h-11" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password" className="mt-1 rounded-xl h-11" />
          </div>
          {err && <p className="text-sm text-destructive" data-testid="login-error">{err}</p>}
          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white btn-lift">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="text-sm text-charcoal/60 mt-5 text-center">
          New here? <Link to="/register" className="text-terracotta hover:underline" data-testid="go-register">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
